import { QueryKey } from '@tanstack/react-query';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ReactNode, useEffect, useRef } from 'react';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import useInfiniteTableData from '@/components/table/hooks/useInfiniteTableData.ts';
import MobileRecordCard from '@/components/table/components/mobile/MobileRecordCard.tsx';
import MobileRowActions from '@/components/table/components/mobile/MobileRowActions.tsx';
import MobileRecordListSkeleton, {
  MobileRecordCardSkeleton,
} from '@/components/table/components/mobile/MobileRecordListSkeleton.tsx';
import EmptyTable from '@/components/table/components/EmptyTable.tsx';
import { exportToExcel } from '@/components/table/_export/TableExport.ts';

/**
 * Tabela poniżej `md`: jedna lista kart zamiast poziomego przewijania.
 * Strony dokładają się po dojściu do wartownika, bo stopka ze strzałkami
 * wymagałaby celowania w dwa małe przyciski.
 */
const MobileRecordList = <T extends RowWithId>({
  queryKey,
  fetchData,
  columns,
  rowContextMenuItems,
  emptyDataComponent,
}: {
  queryKey: QueryKey;
  fetchData: (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ) => Promise<PagedDto<T>>;
  columns: ColumnDef<T>[];
  rowContextMenuItems?: RowContextMenuItemParams<T>[];
  emptyDataComponent?: ReactNode;
}) => {
  const { rows, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteTableData<T>({ queryKey, fetchData });

  const tableDefinition = useTableStoreContext(
    (state) => state.tableDefinition,
  );
  const setExportHandler = useTableStoreContext(
    (state) => state.setExportHandler,
  );

  const table = useReactTable<T>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const hasRows = rows.length > 0;

  // Eksport oddaje strony wczytane do tej pory — tyle samo, co „to, co widać”
  // w stopce desktopu, gdzie arkusz też dostaje jedną stronę.
  useEffect(() => {
    if (!hasRows) {
      setExportHandler(null);
      return;
    }
    setExportHandler(() => exportToExcel(table, tableDefinition));
    return () => setExportHandler(null);
  }, [table, tableDefinition, setExportHandler, hasRows]);

  const sentinelRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((entry) => entry.isIntersecting) &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isPending) return <MobileRecordListSkeleton />;

  if (!hasRows) {
    return <div className="py-10">{emptyDataComponent ?? <EmptyTable />}</div>;
  }

  return (
    <ul className="flex flex-col">
      {table.getRowModel().rows.map((row) => (
        <MobileRecordCard
          key={row.id}
          row={row}
          actions={
            rowContextMenuItems && (
              <MobileRowActions
                rowData={row.original}
                items={rowContextMenuItems}
              />
            )
          }
        />
      ))}
      {hasNextPage && (
        <>
          {isFetchingNextPage && <MobileRecordCardSkeleton />}
          {/* Wartownik osobno od szkieletu, bo `li` w `li` to nieprawidłowy HTML. */}
          <li ref={sentinelRef} aria-hidden className="h-px" />
        </>
      )}
    </ul>
  );
};

export default MobileRecordList;
